import React, { Suspense } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import {
  Navigate,
  useLocation,
  Route,
  Routes,
  BrowserRouter,
} from "react-router-dom";
import { StaticRouter } from "react-router-dom/server";
import axios from "axios";
import Login from "./Login/containers/login";
import ForgotPassword from "./Login/containers/forgotPassword";
import Donors from "./Admin/containers/donors";
import Users from "./Admin/containers/users";
import Donations from "./Admin/containers/donations";
import ImportFile from "./Admin/containers/importFile";
import Academy from "./Admin/containers/academy";
import NotFound from "./toolkit/notFound";
import SuperDonations from "./Admin/containers/Superadmin/donations";
import CONSTANTS from "./utils/constants";
import { firebaseConnect } from "react-redux-firebase";
import Posts from "./Admin/containers/posts";
import Requests from "./Admin/containers/requests";
import Segments from "./Admin/containers/segments";
import InvitePage from "./Login/containers/invitePage";
import ApiKeyManagerContainer from "./Admin/containers/Superadmin/apiKey";
import UrlManagerContainer from "./Admin/containers/Superadmin/urlManager";
import Messaging from "./Admin/containers/messaging";
import Keywords from "./Admin/containers/simpleTextingKeywords";
import AdminMetricsContainer from "./Admin/containers/adminMetrics";
import SuperadminMetricsContainer from "./Admin/containers/Superadmin/metrics";
import IntegrationsContainer from "./Admin/containers/integrations";
import SystemNotificationsContainer from "./Admin/containers/Superadmin/systemNotifications";
import ChatSessionContainer from "./Admin/containers/Superadmin/chatSession";
import PostLoginPhonePromptContainer from "./Admin/containers/postLoginPhonePrompt";
import OnboardingDashboardContainer from "./Admin/containers/charitySetup/onboardingDashboard";
import OnboardingCustomationLayoutContainer from "./Admin/containers/charitySetup/onboardingCustomation";
import GivingSiteCustomizer from "./Admin/components/charitySetup/givingSiteCustomizer";
import SignUpContainer from "./Auth/containers/signUp";
import LoginContainer from "./Auth/containers/login";
import VerifyEmailContainer from "./Auth/containers/verifyEmail";
import EmailSentContainer from "./Auth/containers/emailSent";
import ForgotPasswordContainer from "./Auth/containers/forgot";
import StripeConnectResultContainer from "./Auth/containers/stripeConnect";
import LoadingScreen from "./toolkit/loadingScreen";
import { ONBOARDING_DASHBOARD_PATH } from "./utils/onboarding/stepNavigation";
import firebase from "firebase/compat/app";
import { adminDocumentTitles } from "./utils/adminDocumentTitle";

function LegacyDashboardRedirect() {
  const { pathname, search, hash } = useLocation();
  const nextPath =
    pathname.replace(/^\/dashboard(?=\/|$)/, ONBOARDING_DASHBOARD_PATH) ||
    ONBOARDING_DASHBOARD_PATH;
  return <Navigate to={`${nextPath}${search}${hash}`} replace />;
}

const DnsSetupContainer = React.lazy(() =>
  import("./Auth/containers/dnsSetup")
);

const restClient = axios.create({
  baseURL: CONSTANTS.BASE_URL,
});

const helmetLink = [
  { rel: "canonical", href: "https://www.mahisoft.com" },
  { rel: "apple-touch-icon", sizes: "57x57", href: "/apple-icon-57x57.png" },
  { rel: "apple-touch-icon", sizes: "60x60", href: "/apple-icon-60x60.png" },
  { rel: "apple-touch-icon", sizes: "72x72", href: "/apple-icon-72x72.png" },
  { rel: "apple-touch-icon", sizes: "76x76", href: "/apple-icon-76x76.png" },
  {
    rel: "apple-touch-icon",
    sizes: "114x114",
    href: "/apple-icon-114x114.png",
  },
  {
    rel: "apple-touch-icon",
    sizes: "120x120",
    href: "/apple-icon-120x120.png",
  },
  {
    rel: "apple-touch-icon",
    sizes: "144x144",
    href: "/apple-icon-144x144.png",
  },
  {
    rel: "apple-touch-icon",
    sizes: "152x152",
    href: "/apple-icon-152x152.png",
  },
  {
    rel: "apple-touch-icon",
    sizes: "180x180",
    href: "/apple-icon-180x180.png",
  },
  { rel: "icon", sizes: "192x192", href: "/android-icon-192x192.png" },
  { rel: "icon", sizes: "16x16", href: "/favicon-16x16.png" },
  { rel: "icon", sizes: "32x32", href: "/favicon-32x32.png" },
  { rel: "icon", sizes: "96x96", href: "/favicon-96x96.png" },
  { rel: "icon", href: "/favicon.ico" },
];
function currentHostname(): string {
  if (typeof window === "undefined" || !window.location) {
    return "";
  }
  return window.location.hostname;
}

function currentProjectId(): string | undefined {
  try {
    const options = firebase.app()?.options as
      | { projectId?: string }
      | undefined;
    return options?.projectId;
  } catch {
    return undefined;
  }
}

const AppRoutes = () => {
  const { titleTemplate, defaultTitle } = adminDocumentTitles(
    currentHostname(),
    currentProjectId()
  );
  return (
    <main>
      <Helmet
        titleTemplate={titleTemplate}
        defaultTitle={defaultTitle}
        link={helmetLink}
      />
      <Routes>
        {/* Guest routes - redirect to app if already logged in */}
        <Route path="/" element={<LoginContainer />} />
        <Route path="/login" element={<LoginContainer />} />
        <Route path="/signup" element={<SignUpContainer />} />
        <Route path="/signup/email-sent" element={<EmailSentContainer />} />
        <Route path="/signup/verify" element={<VerifyEmailContainer />} />
        <Route path="/forgot/*" element={<ForgotPasswordContainer />} />
        <Route
          path="/dns-setup"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <DnsSetupContainer />
            </Suspense>
          }
        />
        <Route path="/stripe-connect" element={<StripeConnectResultContainer />} />
        <Route path="/forgotPassword/*" element={<ForgotPassword />} />
        <Route path="/invite/*" element={<InvitePage />} />
        <Route
          path="/setup-phone"
          element={<PostLoginPhonePromptContainer />}
        />

        {/* Protected routes - redirect to login if not authenticated */}
        <Route path="/contacts/*" element={<Donors />} />
        <Route path="/donors/*" element={<Donors />} />
        <Route path="/users/*" element={<Users />} />
        <Route path="/donations/*" element={<Donations />} />
        <Route path="/allDonations/*" element={<SuperDonations />} />
        <Route path="/segments/*" element={<Segments />} />
        <Route path="/viewsparks/*" element={<Posts />} />
        <Route path="/requests/*" element={<Requests />} />
        <Route path="/apiKeys/*" element={<ApiKeyManagerContainer />} />
        <Route path="/urlManager/*" element={<UrlManagerContainer />} />
        <Route path="/messages/*" element={<Messaging />} />
        <Route path="/keywords/*" element={<Keywords />} />
        <Route path="/import/*" element={<ImportFile />} />
        <Route path="/charityMetrics/*" element={<AdminMetricsContainer />} />
        <Route path="/metrics/*" element={<SuperadminMetricsContainer />} />
        <Route path="/academy/*" element={<Academy />} />
        <Route path="/integrations/*" element={<IntegrationsContainer />} />
        <Route
          path="/system-notifications/*"
          element={<SystemNotificationsContainer />}
        />
        <Route path="/chat-session/*" element={<ChatSessionContainer />} />

        {/* TODO: remove this routes after integration with other components */}
        <Route
          path="/onboarding/customation/*"
          element={<OnboardingCustomationLayoutContainer />}
        />
        <Route
          path="/onboarding/*"
          element={<OnboardingDashboardContainer />}
        />
        <Route path="/dashboard/*" element={<LegacyDashboardRedirect />} />
        <Route path="/customizer/*" element={<GivingSiteCustomizer />} />

        {/* 404 - must be last */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </main>
  );
};

const clientRoutes = () => (
  <BrowserRouter>
    <AppRoutes />
  </BrowserRouter>
);
const ClientRoutes = firebaseConnect(["/global/config"])(clientRoutes);

const ServerRoutes = ({ location }) => (
  <StaticRouter location={location}>
    <AppRoutes />
  </StaticRouter>
);

(ServerRoutes as any).propTypes = {
  location: PropTypes.string.isRequired,
};

export default ClientRoutes;
export { ServerRoutes, restClient };
